/**
 * The fixed outcome in a verdict card's "↳ then" clause — where an optimizer
 * rule has an action picker, a verdict has one answer (FAIL, performing,
 * hold, halt), drawn in the picker's own shape so the cards read alike.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import type { LucideIcon } from 'lucide-react';

import { cn } from '@/lib/utils';

import type { RuleActionTone } from '../../rules/rule-surfaces';

const TONE: Record<RuleActionTone, string> = {
  destructive: 'border-destructive/40 bg-destructive/10 text-destructive',
  warning: 'border-warning/40 bg-warning/10 text-warning',
  success: 'border-success/40 bg-success/10 text-success',
};

export interface VerdictOutcomeProps {
  tone: RuleActionTone;
  icon: LucideIcon;
  label: string;
}

export const VerdictOutcome: FC<VerdictOutcomeProps> = ({ tone, icon: Icon, label }) => (
  <span className={cn('inline-flex h-7 items-center gap-1.5 rounded-md border px-2 text-[12px] font-semibold', TONE[tone])} data-testid="verdict-outcome">
    <Icon className="h-3.5 w-3.5" aria-hidden />
    {label}
  </span>
);

/** The clause's edge, the Flow Optimizer's way: red takes away, amber holds, green favours. */
export const outcomeEdge = (tone: RuleActionTone): string =>
  tone === 'destructive' ? 'border-l-destructive bg-destructive/5' : tone === 'success' ? 'border-l-success bg-success/5' : 'border-l-warning bg-warning/5';
